import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SendIcon from '@mui/icons-material/Send';
import { IconButton, Tooltip } from '@mui/material';
import { apiService, getApiErrorMessage } from '../services/apiService';
import { authService } from '../services/authService';
import { permissionService, Permissions } from '../services/permissionService';
import {
  TaskStatus,
  TaskManagerBoard,
  TaskManagerBoardListItem,
  taskManagerService,
} from '../services/taskManagerService';

interface UserOption {
  id: string;
  userName: string;
  displayName: string;
}

interface TaskDialogValue {
  title: string;
  description: string;
  storyId: string;
  assigneeId: string;
}

interface TaskDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  stories: Array<{ id: string; title: string }>;
  users: UserOption[];
  initialValue: TaskDialogValue;
  mode: 'create' | 'edit';
  currentUserId?: string;
  comments?: Array<{ id: string; userId: string; userName: string; comment: string; createdAt: string }>;
  onAddComment?: (comment: string) => Promise<void>;
  onEditComment?: (commentId: string, comment: string) => Promise<void>;
  onDeleteComment?: (commentId: string) => Promise<void>;
  onSave: (value: TaskDialogValue) => void;
}

const KANBAN_COLUMNS: Array<{ key: 'story' | TaskStatus; label: string }> = [
  { key: 'story', label: 'Story' },
  { key: 'new', label: 'Új' },
  { key: 'inProgress', label: 'Folyamatban' },
  { key: 'blocked', label: 'Elakadt' },
  { key: 'readyForReview', label: 'Megszakítva' },
  { key: 'done', label: 'Kész' },
];

const normalizeString = (value: string | undefined | null): string => (value || '').trim();
const normalizeMentionKey = (value: string | undefined | null): string =>
  (value || '').trim().replace(/\s+/g, '_').toLowerCase();
const toDateOnly = (value?: string | null): string | null => {
  if (!value) {
    return null;
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const extractArrayData = (response: any): any[] => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  return [];
};

const TaskDialog: React.FC<TaskDialogProps> = ({
  open,
  onClose,
  title,
  stories,
  users,
  initialValue,
  mode,
  currentUserId,
  comments = [],
  onAddComment,
  onEditComment,
  onDeleteComment,
  onSave,
}) => {
  const [value, setValue] = useState<TaskDialogValue>(initialValue);
  const [commentText, setCommentText] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editingCommentText, setEditingCommentText] = useState('');

  useEffect(() => {
    if (open) {
      setValue(initialValue);
      setCommentText('');
      setEditingCommentId(null);
      setEditingCommentText('');
    }
  }, [open, initialValue]);

  const mentionQuery = useMemo(() => {
    const match = commentText.match(/(?:^|\s)@([^\s@]*)$/);
    return match ? (match[1] ?? '') : null;
  }, [commentText]);

  const mentionCandidates = useMemo(() => {
    if (mentionQuery == null) {
      return [] as UserOption[];
    }

    const normalized = normalizeMentionKey(mentionQuery);
    const filtered = users.filter((user) => {
      if (!normalizeString(user.userName)) {
        return false;
      }

      const byDisplay = normalizeMentionKey(user.displayName).startsWith(normalized);
      const byUserName = normalizeMentionKey(user.userName).startsWith(normalized);
      return byDisplay || byUserName;
    });

    return filtered.slice(0, 6);
  }, [users, mentionQuery]);

  const handleSave = () => {
    if (!normalizeString(value.title) || !value.storyId) {
      return;
    }
    onSave({
      ...value,
      title: normalizeString(value.title),
      description: normalizeString(value.description),
    });
  };

  const insertMention = (userName: string) => {
    const mentionToken = (userName || '').trim().replace(/\s+/g, '_');
    const updated = commentText.replace(/(?:^|\s)@([^\s@]*)$/, (fullMatch) => {
      const hasLeadingSpace = fullMatch.startsWith(' ');
      return `${hasLeadingSpace ? ' ' : ''}@${mentionToken} `;
    });

    setCommentText(updated);
  };

  const handleAddComment = async () => {
    if (!onAddComment) {
      return;
    }

    const normalized = normalizeString(commentText);
    if (!normalized) {
      return;
    }

    try {
      setSubmittingComment(true);
      await onAddComment(normalized);
      setCommentText('');
    } finally {
      setSubmittingComment(false);
    }
  };

  const startEditComment = (commentId: string, currentText: string) => {
    setEditingCommentId(commentId);
    setEditingCommentText(currentText);
  };

  const cancelEditComment = () => {
    setEditingCommentId(null);
    setEditingCommentText('');
  };

  const saveEditComment = async () => {
    if (!onEditComment || !editingCommentId) {
      return;
    }

    const normalized = normalizeString(editingCommentText);
    if (!normalized) {
      return;
    }

    await onEditComment(editingCommentId, normalized);
    cancelEditComment();
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!onDeleteComment) {
      return;
    }

    const confirmed = window.confirm('Biztosan törölni szeretnéd ezt a kommentet?');
    if (!confirmed) {
      return;
    }

    await onDeleteComment(commentId);
    if (editingCommentId === commentId) {
      cancelEditComment();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Task név"
            value={value.title}
            onChange={(event) => setValue((prev) => ({ ...prev, title: event.target.value }))}
            fullWidth
            required
          />

          <TextField
            label="Leírás"
            value={value.description}
            onChange={(event) => setValue((prev) => ({ ...prev, description: event.target.value }))}
            fullWidth
            multiline
            minRows={3}
          />

          <TextField
            select
            fullWidth
            label="Story"
            value={value.storyId}
            onChange={(event) => setValue((prev) => ({ ...prev, storyId: event.target.value }))}
          >
            {stories.map((story) => (
              <MenuItem key={story.id} value={story.id}>
                {story.title}
              </MenuItem>
            ))}
          </TextField>

          <Autocomplete
            options={users}
            value={users.find((user) => user.id === value.assigneeId) || null}
            onChange={(_, selectedUser) => setValue((prev) => ({ ...prev, assigneeId: selectedUser?.id || '' }))}
            getOptionLabel={(option) => option.displayName}
            isOptionEqualToValue={(option, selectedUser) => option.id === selectedUser.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Felelős"
                placeholder="Kezdjen el gépelni a kereséshez..."
              />
            )}
            clearOnEscape
            noOptionsText="Nincs találat"
          />

          {mode === 'edit' && (
            <>
              <Divider />
              <Typography variant="subtitle2">Kommentek</Typography>

              <Box sx={{ maxHeight: 260, overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
                {comments.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                    Még nincs komment ehhez a taskhoz.
                  </Typography>
                ) : (
                  <Stack spacing={1}>
                    {comments.map((comment) => (
                      <Card key={comment.id} variant="outlined" sx={{ backgroundColor: '#fafafa' }}>
                        <CardContent sx={{ p: 1.25, '&:last-child': { pb: 1.25 } }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">
                              {`${comment.userName} • ${new Date(comment.createdAt).toLocaleString('hu-HU')}`}
                            </Typography>

                            {comment.userId === currentUserId && editingCommentId !== comment.id && (
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Tooltip title="Komment szerkesztése">
                                  <IconButton
                                    size="small"
                                    onClick={() => startEditComment(comment.id, comment.comment)}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Komment törlése">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleDeleteComment(comment.id)}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            )}
                          </Box>

                          {editingCommentId === comment.id ? (
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                              <TextField
                                size="small"
                                fullWidth
                                value={editingCommentText}
                                onChange={(event) => setEditingCommentText(event.target.value)}
                              />
                              <Button size="small" variant="contained" onClick={saveEditComment}>
                                Mentés
                              </Button>
                              <Button size="small" variant="text" onClick={cancelEditComment}>
                                Mégse
                              </Button>
                            </Stack>
                          ) : (
                            <Typography variant="body2" color="text.primary">
                              {comment.comment}
                            </Typography>
                          )}

                        </CardContent>
                      </Card>
                    ))}
                  </Stack>
                )}
              </Box>

              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Új komment"
                value={commentText}
                onChange={(event) => setCommentText(event.target.value)}
                placeholder="Írj kommentet, és említs felhasználót @felhasznalonev formában"
              />

              {mentionCandidates.length > 0 && (
                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
                  {mentionCandidates.map((user) => (
                    <Chip
                      key={user.id}
                      label={`@${(user.userName || '').replace(/\s+/g, '_')}`}
                      size="small"
                      onClick={() => insertMention(user.userName)}
                      clickable
                    />
                  ))}
                </Stack>
              )}

              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  startIcon={<SendIcon />}
                  onClick={handleAddComment}
                  disabled={!normalizeString(commentText) || submittingComment}
                >
                  Komment küldése
                </Button>
              </Box>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Mégse</Button>
        <Button onClick={handleSave} variant="contained" disabled={!normalizeString(value.title) || !value.storyId}>
          Mentés
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const TaskManagerPageBackend: React.FC = () => {
  const currentUser = authService.getCurrentUser();
  const currentUserId = currentUser?.userId || '';

  const [boardList, setBoardList] = useState<TaskManagerBoardListItem[]>([]);
  const [selectedBoardId, setSelectedBoardId] = useState<string | null>(null);
  const [selectedBoard, setSelectedBoard] = useState<TaskManagerBoard | null>(null);

  const [users, setUsers] = useState<UserOption[]>([]);
  const [selectedAssigneeId, setSelectedAssigneeId] = useState('');
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [deadlineFrom, setDeadlineFrom] = useState('');
  const [deadlineTo, setDeadlineTo] = useState('');
  const [showAllTasks, setShowAllTasks] = useState(false);
  const [showMyTasks, setShowMyTasks] = useState(true);
  const [canViewAllTasks, setCanViewAllTasks] = useState(
    permissionService.hasPermission(Permissions.ViewAllTaskManagerTasks)
  );

  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [taskDialogTitle, setTaskDialogTitle] = useState('Új task');
  const [taskDialogValue, setTaskDialogValue] = useState<TaskDialogValue>({
    title: '',
    description: '',
    storyId: '',
    assigneeId: '',
  });
  const [editingTaskId, setEditingTaskId] = useState<string | null>(null);

  const [draggedTaskId, setDraggedTaskId] = useState<string | null>(null);
  const [collapsedStories, setCollapsedStories] = useState<Record<string, boolean>>({});

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false,
    message: '',
    severity: 'info',
  });

  const showError = (error: any, fallbackMessage: string) => {
    const message = getApiErrorMessage(error, fallbackMessage);
    setSnackbar({ open: true, message, severity: 'error' });
  };

  const loadBoardList = async () => {
    try {
      const boards = await taskManagerService.getBoards();
      const sortedBoards = [...boards].sort((a, b) =>
        b.munkaszam.localeCompare(a.munkaszam, 'hu-HU', { numeric: true, sensitivity: 'base' })
      );
      setBoardList(sortedBoards);
    } catch (error) {
      showError(error, 'Nem sikerült betölteni a Task Manager rekordokat.');
      setBoardList([]);
    }
  };

  const loadBoardDetail = async (boardId: string) => {
    try {
      const detail = await taskManagerService.getBoardById(boardId);
      setSelectedBoard(detail);
    } catch (error) {
      showError(error, 'Nem sikerült betölteni a board részleteit.');
      setSelectedBoard(null);
    }
  };

  useEffect(() => {
    loadBoardList();
  }, []);

  useEffect(() => {
    const refreshPermission = () => {
      const hasPermission = permissionService.hasPermission(Permissions.ViewAllTaskManagerTasks);
      setCanViewAllTasks(hasPermission);
      if (!hasPermission) {
        setShowAllTasks(false);
      }
    };

    refreshPermission();
    const unsubscribe = permissionService.onPermissionsChanged(refreshPermission);
    return unsubscribe;
  }, []);

  useEffect(() => {
    const loadUsers = async () => {
      try {
        const usersResponse = await apiService.get('/users?activeOnly=true');
        const mapped = extractArrayData(usersResponse).map((item: any) => ({
          id: String(item.id || item.userId || item.userID || item.userid || item.userName),
          userName: String(item.userName || item.UserName || ''),
          displayName: String(item.displayName || item.DisplayName || item.userName || item.UserName || ''),
        }));

        const uniqueById = new Map<string, UserOption>();
        mapped.forEach((user) => {
          if (!user.id) return;
          uniqueById.set(user.id, {
            id: user.id,
            userName: user.userName,
            displayName: user.displayName || user.userName,
          });
        });

        setUsers(Array.from(uniqueById.values()));
      } catch (error) {
        showError(error, 'Nem sikerült betölteni a felhasználókat.');
        setUsers([]);
      }
    };

    loadUsers();
  }, []);

  useEffect(() => {
    if (selectedBoardId) {
      loadBoardDetail(selectedBoardId);
    }
  }, [selectedBoardId]);

  const openTaskEditDialog = (task: any) => {
    setTaskDialogTitle('Task szerkesztése');
    setTaskDialogValue({
      title: task.title,
      description: task.description,
      storyId: task.storyId,
      assigneeId: task.assigneeId || '',
    });
    setEditingTaskId(task.id);
    setTaskDialogOpen(true);
  };

  const handleSaveTask = async (value: TaskDialogValue) => {
    if (!selectedBoardId || !selectedBoard) return;

    try {
      if (!editingTaskId) {
        return;
      }

      const existing = selectedBoard.tasks.find((task) => task.id === editingTaskId);
      await taskManagerService.updateTask(selectedBoardId, editingTaskId, {
        storyId: value.storyId,
        title: value.title,
        description: value.description,
        status: existing?.status || 'new',
        assigneeId: value.assigneeId || undefined,
      });

      setTaskDialogOpen(false);
      setEditingTaskId(null);
      await loadBoardDetail(selectedBoardId);
      await loadBoardList();
    } catch (error) {
      showError(error, 'Nem sikerült menteni a taskot.');
    }
  };

  const handleAddTaskComment = async (comment: string) => {
    if (!selectedBoardId || !editingTaskId) {
      return;
    }

    try {
      await taskManagerService.addTaskComment(selectedBoardId, editingTaskId, comment);
      await loadBoardDetail(selectedBoardId);
      await loadBoardList();
      setSnackbar({ open: true, message: 'Komment mentve.', severity: 'success' });
    } catch (error) {
      showError(error, 'Nem sikerült menteni a kommentet.');
    }
  };

  const handleEditTaskComment = async (commentId: string, comment: string) => {
    if (!selectedBoardId || !editingTaskId) {
      return;
    }

    try {
      await taskManagerService.updateTaskComment(selectedBoardId, editingTaskId, commentId, comment);
      await loadBoardDetail(selectedBoardId);
      await loadBoardList();
      setSnackbar({ open: true, message: 'Komment frissítve.', severity: 'success' });
    } catch (error) {
      showError(error, 'Nem sikerült frissíteni a kommentet.');
    }
  };

  const handleDeleteTaskComment = async (commentId: string) => {
    if (!selectedBoardId || !editingTaskId) {
      return;
    }

    try {
      await taskManagerService.deleteTaskComment(selectedBoardId, editingTaskId, commentId);
      await loadBoardDetail(selectedBoardId);
      await loadBoardList();
      setSnackbar({ open: true, message: 'Komment törölve.', severity: 'success' });
    } catch (error) {
      showError(error, 'Nem sikerült törölni a kommentet.');
    }
  };

  const handleInlineAssign = async (taskId: string, assigneeId: string) => {
    if (!selectedBoardId) return;

    try {
      await taskManagerService.assignTask(selectedBoardId, taskId, assigneeId);
      await loadBoardDetail(selectedBoardId);
    } catch (error) {
      showError(error, 'Nem sikerült hozzárendelni a taskot.');
    }
  };

  const toggleStoryCollapsed = (storyId: string) => {
    setCollapsedStories((prev) => ({
      ...prev,
      [storyId]: !prev[storyId],
    }));
  };

  const handleDropToCell = async (storyId: string, status: TaskStatus) => {
    if (!selectedBoardId || !draggedTaskId) return;

    try {
      await taskManagerService.moveTask(selectedBoardId, draggedTaskId, storyId, status);
      setDraggedTaskId(null);
      await loadBoardDetail(selectedBoardId);
      await loadBoardList();
    } catch (error) {
      showError(error, 'Nem sikerült mozgatni a taskot.');
    }
  };

  const getStoryTitle = (storyId: string): string => {
    return selectedBoard?.stories.find((story) => story.id === storyId)?.title || 'Story nélkül';
  };

  const getTasksByStoryAndStatus = (storyId: string, status: TaskStatus) => {
    if (!selectedBoard) return [];
    return selectedBoard.tasks.filter((task) => task.storyId === storyId && task.status === status);
  };

  const selectedBoardListItem = useMemo(
    () => boardList.find((board) => board.id === selectedBoardId) || null,
    [boardList, selectedBoardId]
  );

  const visibleStories = useMemo(() => {
    if (!selectedBoard) {
      return [];
    }

    return selectedBoard.stories.filter((story) =>
      selectedBoard.tasks.some((task) => task.storyId === story.id)
    );
  }, [selectedBoard]);

  const editingTask = useMemo(() => {
    if (!selectedBoard || !editingTaskId) {
      return null;
    }

    return selectedBoard.tasks.find((task) => task.id === editingTaskId) || null;
  }, [selectedBoard, editingTaskId]);

  const taskPassesFilters = useMemo(() => {
    return (task: TaskManagerBoardListItem['taskFilters'][number]): boolean => {
      if (!showAllTasks && !showMyTasks) {
        return false;
      }

      const assigneeId = task.assigneeUserId || '';
      if (selectedAssigneeId && assigneeId !== selectedAssigneeId) {
        return false;
      }

      const taskDueDate = toDateOnly(task.dueDate);
      if (deadlineFrom) {
        if (!taskDueDate || taskDueDate < deadlineFrom) {
          return false;
        }
      }
      if (deadlineTo) {
        if (!taskDueDate || taskDueDate > deadlineTo) {
          return false;
        }
      }

      if (showAllTasks) {
        return canViewAllTasks;
      }

      return showMyTasks && !!currentUserId && assigneeId === currentUserId;
    };
  }, [canViewAllTasks, currentUserId, deadlineFrom, deadlineTo, selectedAssigneeId, showAllTasks, showMyTasks]);

  const boardHasMatchingTask = useMemo(() => {
    return (board: TaskManagerBoardListItem): boolean => {
      if (!board.taskFilters || board.taskFilters.length === 0) {
        return false;
      }

      return board.taskFilters.some((task) => taskPassesFilters(task));
    };
  }, [taskPassesFilters]);

  const projectFilterOptions = useMemo(
    () =>
      boardList.filter((board) => {
        if (selectedProjectId && board.id === selectedProjectId) {
          return true;
        }
        return boardHasMatchingTask(board);
      }),
    [boardHasMatchingTask, boardList, selectedProjectId]
  );

  const filteredBoardList = useMemo(() => {
    return boardList.filter((board) => {
      if (selectedProjectId && board.id !== selectedProjectId) {
        return false;
      }
      return boardHasMatchingTask(board);
    });
  }, [boardHasMatchingTask, boardList, selectedProjectId]);

  if (!selectedBoardId || !selectedBoard) {
    return (
      <Box
        sx={{
          flexGrow: 1,
          minHeight: 0,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: '#f0f3f6',
          p: 2,
          gap: 2,
          overflow: 'hidden',
        }}
      >
        <Card sx={{ backgroundColor: 'white', boxShadow: 1 }}>
          <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
              <Autocomplete
                options={users}
                sx={{ minWidth: 260 }}
                value={users.find((user) => user.id === selectedAssigneeId) || null}
                onChange={(_, selectedUser) => {
                  setSelectedAssigneeId(selectedUser?.id || '');
                  setSelectedProjectId('');
                }}
                getOptionLabel={(option) => option.displayName}
                isOptionEqualToValue={(option, selectedUser) => option.id === selectedUser.id}
                renderInput={(params) => <TextField {...params} size="small" label="Felelős szűrő" placeholder="Mindenki" />}
                noOptionsText="Nincs találat"
              />

              <Autocomplete
                options={projectFilterOptions}
                sx={{ minWidth: 280 }}
                value={projectFilterOptions.find((item) => item.id === selectedProjectId) || null}
                onChange={(_, selectedProject) => setSelectedProjectId(selectedProject?.id || '')}
                getOptionLabel={(option) => `${option.munkaszam}${option.munkaTargya ? ` - ${option.munkaTargya}` : ''}`}
                isOptionEqualToValue={(option, selectedProject) => option.id === selectedProject.id}
                renderInput={(params) => <TextField {...params} size="small" label="Projekt szűrő" placeholder="Összes projekt" />}
                noOptionsText="Nincs találat"
              />

              <TextField
                size="small"
                type="date"
                label="Határidő -tól"
                value={deadlineFrom}
                onChange={(event) => setDeadlineFrom(event.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                size="small"
                type="date"
                label="Határidő -ig"
                value={deadlineTo}
                onChange={(event) => setDeadlineTo(event.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
              {canViewAllTasks && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showAllTasks}
                      onChange={(event) => setShowAllTasks(event.target.checked)}
                    />
                  }
                  label="Összes feladat megjelenítése"
                />
              )}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showMyTasks}
                    onChange={(event) => setShowMyTasks(event.target.checked)}
                  />
                }
                label="Saját feladataim megjelenítése"
              />

              <Button
                variant="outlined"
                onClick={() => {
                  setSelectedAssigneeId('');
                  setSelectedProjectId('');
                  setDeadlineFrom('');
                  setDeadlineTo('');
                  setShowAllTasks(false);
                  setShowMyTasks(true);
                }}
              >
                Szűrők törlése
              </Button>
            </Box>

          </CardContent>
        </Card>

        <Card sx={{ backgroundColor: 'white', boxShadow: 1, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1, minHeight: 0 }}>
            <Typography variant="h6">Task Manager</Typography>

            <TableContainer component={Paper} sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Munkaszám</strong></TableCell>
                    <TableCell><strong>Munka tárgya</strong></TableCell>
                    <TableCell><strong>Létrehozva</strong></TableCell>
                    <TableCell><strong>Új / Folyamatban / Kész task</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredBoardList.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        Nincs a szűrőknek megfelelő Task Manager rekord.
                      </TableCell>
                    </TableRow>
                  )}
                  {filteredBoardList.map((record) => (
                    <TableRow key={record.id} hover sx={{ cursor: 'pointer' }} onClick={() => setSelectedBoardId(record.id)}>
                      <TableCell>{record.munkaszam}</TableCell>
                      <TableCell>{record.munkaTargya || '-'}</TableCell>
                      <TableCell>{new Date(record.createdAt).toLocaleDateString('hu-HU')}</TableCell>
                      <TableCell>{record.newCount} / {record.inProgressCount} / {record.doneCount}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}>
          <Alert severity={snackbar.severity} onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flexGrow: 1,
        minHeight: 0,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#f0f3f6',
        p: 2,
        gap: 2,
        overflow: 'hidden',
      }}
    >
      <Card sx={{ backgroundColor: 'white', boxShadow: 1 }}>
        <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => { setSelectedBoardId(null); setSelectedBoard(null); }}>
              Vissza
            </Button>
            <Typography variant="h6">
              {selectedBoardListItem?.munkaszam || selectedBoard.munkaszam}
              {(selectedBoardListItem?.munkaTargya || selectedBoard.munkaTargya) ? ` - ${selectedBoardListItem?.munkaTargya || selectedBoard.munkaTargya}` : ''}
            </Typography>
          </Box>

          <Box />
        </CardContent>
      </Card>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', pb: 1 }}>
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(6, minmax(0, 1fr))', gap: 1.5 }}>
            {KANBAN_COLUMNS.map((column) => (
              <Paper key={column.key} sx={{ p: 1.5, backgroundColor: '#ffffff' }}>
                <Typography variant="subtitle1" align="center" sx={{ fontWeight: 700 }}>
                  {column.label}
                </Typography>
              </Paper>
            ))}
          </Box>

          {visibleStories.length === 0 && (
            <Paper sx={{ p: 2, backgroundColor: '#ffffff' }}>
              <Typography variant="body2" color="text.secondary">Még nincs story.</Typography>
            </Paper>
          )}

          {visibleStories.map((story) => (
            <Box key={story.id} sx={{ display: 'grid', gridTemplateColumns: 'repeat(6, minmax(0, 1fr))', gap: 1.5 }}>
              <Paper sx={{ p: 1.5, backgroundColor: '#ffffff', minHeight: 120 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body1" sx={{ fontWeight: 700 }}>{story.title}</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Tooltip title={collapsedStories[story.id] ? 'Story kinyitása' : 'Story összecsukása'}>
                      <IconButton size="small" onClick={() => toggleStoryCollapsed(story.id)}>
                        {collapsedStories[story.id] ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </Paper>

              {(KANBAN_COLUMNS.filter((column) => column.key !== 'story') as Array<{ key: TaskStatus; label: string }>).map((column) => (
                <Paper
                  key={`${story.id}-${column.key}`}
                  sx={{ p: 1, backgroundColor: '#ffffff', minHeight: 120 }}
                  onDragOver={(event) => event.preventDefault()}
                  onDrop={() => handleDropToCell(story.id, column.key)}
                >
                  {collapsedStories[story.id] ? (
                    <Typography variant="caption" color="text.secondary">
                      Story összecsukva
                    </Typography>
                  ) : (
                  <Stack spacing={1}>
                    {getTasksByStoryAndStatus(story.id, column.key).map((task) => (
                      <Card
                        key={task.id}
                        variant="outlined"
                        draggable
                        onDragStart={() => setDraggedTaskId(task.id)}
                        onClick={() => openTaskEditDialog(task)}
                        sx={{ cursor: 'pointer', '&:hover': { borderColor: 'primary.main' } }}
                      >
                        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                          <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>{task.title}</Typography>

                          <Chip size="small" label={getStoryTitle(task.storyId)} sx={{ mb: 1 }} />

                          {task.path && <Chip size="small" color="default" label={task.path} sx={{ mb: 1 }} />}

                          <Autocomplete
                            size="small"
                            options={users}
                            value={users.find((user) => user.id === (task.assigneeId || '')) || null}
                            onChange={(_, selectedUser) => handleInlineAssign(task.id, selectedUser?.id || '')}
                            getOptionLabel={(option) => option.displayName}
                            isOptionEqualToValue={(option, selectedUser) => option.id === selectedUser.id}
                            onClick={(event) => event.stopPropagation()}
                            onMouseDown={(event) => event.stopPropagation()}
                            renderInput={(params) => <TextField {...params} label="Felelős" placeholder="Keresés..." />}
                            noOptionsText="Nincs találat"
                          />
                        </CardContent>
                      </Card>
                    ))}
                  </Stack>
                  )}
                </Paper>
              ))}
            </Box>
          ))}
        </Box>
      </Box>

      <TaskDialog
        open={taskDialogOpen}
        onClose={() => { setTaskDialogOpen(false); setEditingTaskId(null); }}
        title={taskDialogTitle}
        stories={selectedBoard.stories}
        users={users}
        initialValue={taskDialogValue}
        mode={editingTaskId ? 'edit' : 'create'}
        currentUserId={currentUserId}
        comments={(editingTask?.comments || []).map((comment) => ({
          id: comment.id,
          userId: comment.userId,
          userName: comment.userName,
          comment: comment.comment,
          createdAt: comment.createdAt,
        }))}
        onAddComment={editingTaskId ? handleAddTaskComment : undefined}
        onEditComment={editingTaskId ? handleEditTaskComment : undefined}
        onDeleteComment={editingTaskId ? handleDeleteTaskComment : undefined}
        onSave={handleSaveTask}
      />

      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TaskManagerPageBackend;
